import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Puzzle } from '../../shared/types';
import {
  Attempt, Cell, Deal, DeckBoard, DrillMode, DrillModeId, DrillTotals, MODES, ScoredRun,
  SESSION_LENGTH, WRONG_PENALTY_MS, addAttempt, bestRuns, createDealer, finishSession, flushPending,
  formatMs, isEchoTap, isSprint, isWanted, loadDeck, loadTotals, migrateLocalHistory, queuePending,
  saveTotals, solutionCells, summarize, toBoardRecord,
} from '../drill';
import { api } from '../api';
import { getPlayerToken } from '../cookie';
import { PracticeView } from './PracticeView';
import { StaticBoard } from './StaticBoard';

interface Props {
  mode: DrillMode;
  onChangeMode: (id: DrillModeId) => void;
  onOpenHistory: () => void;
  onBack: () => void;
}

const cellKey = (c: Cell) => `${c.row},${c.col}`;

/**
 * Owns its own ticking so the board and the log don't re-render 20 times a second behind it.
 */
function LiveTimer({ startedAt, running, banked = 0, className = 'drill-timer' }: {
  startedAt: number;
  running: boolean;
  /** Time already on the clock from earlier boards, for the session total. */
  banked?: number;
  className?: string;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 47);
    return () => clearInterval(id);
  }, [running, startedAt]);
  const elapsed = Math.max(0, (running ? now : startedAt) - startedAt);
  return <span className={className}>{formatMs(banked + elapsed)}</span>;
}

/** What is left of a sprint's clock. Turns urgent under five seconds. */
function Countdown({ endsAt }: { endsAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 47);
    return () => clearInterval(id);
  }, [endsAt]);
  const left = Math.max(0, endsAt - now);
  return <span className={`drill-timer${left <= 5_000 ? ' is-low' : ''}`}>{formatMs(left)}</span>;
}

/**
 * One pip per board still to find. Only a correct answer fills one — a miss or a skip costs time,
 * not a pip, so what is left to do is always what is left unlit.
 */
function Pips({ found }: { found: number }) {
  return (
    <div className="drill-pips" aria-label={`${found} of ${SESSION_LENGTH} found`}>
      {Array.from({ length: SESSION_LENGTH }, (_, i) => (
        <span key={i} className={`drill-pip ${i < found ? 'is-right' : i === found ? 'is-current' : ''}`} />
      ))}
    </div>
  );
}

const ModePicker = memo(function ModePicker({ mode, onChange }: {
  mode: DrillMode; onChange: (id: DrillModeId) => void;
}) {
  return (
    <div className="drill-modes" role="tablist" aria-label="Tek Mode version">
      {MODES.map(m => (
        <button
          key={m.id}
          role="tab"
          aria-selected={m.id === mode.id}
          className={`drill-mode${m.id === mode.id ? ' is-on' : ''}`}
          onClick={() => onChange(m.id)}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
});

const LogStrip = memo(function LogStrip({ log, onReview }: { log: Attempt[]; onReview: (a: Attempt) => void }) {
  // Nothing at all until a board has been answered: an empty strip explaining itself is a line of
  // text where the boards are about to be.
  if (log.length === 0) return null;
  return (
    <div className="drill-log">
      {log.map((a, i) => (
        <button
          key={log.length - i}
          className={`drill-log-item${a.correct ? '' : a.skipped ? ' is-skipped' : ' is-wrong'}`}
          onClick={() => onReview(a)}
          title={`${a.correct ? 'Right' : a.skipped ? 'Skipped' : 'Wrong'} · ${formatMs(a.ms)}`}
        >
          <span className="drill-thumb"><StaticBoard regions={a.regions} /></span>
          <span className="drill-log-meta">
            <span className={`drill-mark${a.correct ? '' : a.skipped ? ' is-skipped' : ' is-wrong'}`}>
              {a.correct ? '✓' : a.skipped ? '–' : '✕'}
            </span>
            {formatMs(a.ms)}
          </span>
        </button>
      ))}
    </div>
  );
});

/**
 * Rapid-fire practice on finding where the queens go.
 *
 * Every board is a real puzzle with one solution and no giveaway one-square region; what counts as
 * an answer is the version's business — one queen, two queens, or the first and last rows' queens,
 * which are the two that pin a 5×5 down on their own. A wrong queen ends the board immediately.
 *
 * A session runs until five boards are **found**, and is scored on the clock across it. Neither a
 * miss nor a skip shortens that — another board just takes its place — so what separates two runs
 * is time: a skip costs only the seconds already spent on the board, a miss costs those plus
 * `WRONG_PENALTY_MS`. Every board is one tap per queen; the drill is reading, not solving.
 * Anything missed or skipped can be opened afterwards as a full practice board (`PracticeView`)
 * and played properly, timer and hints and all.
 *
 * Every finished session is recorded server-side — per-board times against the board that was on
 * screen — which is what the records here are read from and what the modelling work reads. None of
 * it reaches the player's record: a drill answer is not a solve.
 */
export function DrillView({ mode, onChangeMode, onOpenHistory, onBack }: Props) {
  const [deal, setDeal] = useState<Deal | null>(null);
  const [shownAt, setShownAt] = useState(0);
  const [log, setLog] = useState<Attempt[]>([]); // newest first
  const [totals, setTotals] = useState<DrillTotals>(() => loadTotals(mode.id));
  const [flash, setFlash] = useState<'right' | 'wrong' | 'skip' | null>(null);
  const [review, setReview] = useState<Attempt | null>(null);
  /**
   * Which of the boards that beat you is open as a full game, as an index into `unfinished` — an
   * index rather than the board itself so the finish screen can hand over the next one.
   */
  const [practiceAt, setPracticeAt] = useState<number | null>(null);
  /**
   * Every run this player has finished, every version of it: read once and kept across version
   * switches, so changing board size re-reads a list already in hand instead of blanking the
   * record cards for the length of a round trip. Runs finished here join it as they happen.
   */
  const [runs, setRuns] = useState<ScoredRun[]>([]);
  /** The square a refused tap landed on, and a counter so a repeat restarts the flash. */
  const [ignored, setIgnored] = useState<{ row: number; col: number; n: number } | null>(null);
  /** The prebuilt boards, for the versions dealt from a deck. Null until the fetch lands. */
  const [deck, setDeck] = useState<DeckBoard[] | null>(null);
  const [deckError, setDeckError] = useState(false);
  /** Bumped to ask for the deck again after a failed fetch. */
  const [deckTry, setDeckTry] = useState(0);

  /** The last square actually answered on, for `isEchoTap`. A ref: it drives no rendering. */
  const lastPick = useRef<{ cell: Cell; at: number } | null>(null);
  /** When a sprint's clock runs out. Wrong answers pull it closer; null outside a sprint. */
  const [endsAt, setEndsAt] = useState<number | null>(null);

  // Rebuilt per version: a 4×4 deck and a 5×5 generator are different things, and the deck is kept
  // across sessions so a second session doesn't re-deal the first one's boards.
  const dealerRef = useRef<ReturnType<typeof createDealer> | null>(null);

  const sprint = isSprint(mode);
  const stats = useMemo(() => summarize(log), [log]);
  const finished = !deal && (sprint ? log.length > 0 : stats.correct >= SESSION_LENGTH);

  // Switching version is a fresh scoreboard, not a continuation of the last one.
  useEffect(() => {
    dealerRef.current = null;
    setDeal(null);
    setLog([]);
    setFlash(null);
    setEndsAt(null);
    setReview(null);
    setPracticeAt(null);
    setTotals(loadTotals(mode.id));
  }, [mode.id]);

  // The deck versions deal from a file rather than from a generator, so it has to be here before a
  // session can start. Fetched once per page load and kept, so switching back is instant.
  useEffect(() => {
    if (!mode.deck) { setDeck(null); setDeckError(false); return; }
    let live = true;
    setDeckError(false);
    loadDeck()
      .then(boards => { if (live) setDeck(boards); })
      .catch(() => { if (live) setDeckError(true); });
    return () => { live = false; };
  }, [mode.deck, mode.id, deckTry]);

  /** Is there anything to deal? Only the deck versions can answer no. */
  const ready = !mode.deck || deck !== null;

  function nextBoard() {
    if (!dealerRef.current) dealerRef.current = createDealer(mode, Math.random, deck ?? undefined);
    setDeal(dealerRef.current());
    setIgnored(null);
    setShownAt(Date.now());
  }

  function startSession() {
    if (!ready) return; // the deck versions have nothing to deal until their fetch lands
    setLog([]);
    setFlash(null);
    setEndsAt(mode.limitMs === undefined ? null : Date.now() + mode.limitMs);
    nextBoard();
  }

  /**
   * Book a finished run: the session record goes to the server, the totals get their tally, and
   * the drill drops to its summary.
   */
  function endRun(finalLog: Attempt[], nextTotals: DrillTotals) {
    const s = summarize(finalLog);
    // A session's score is its clock; a sprint's is boards found, and its penalties were paid out
    // of the countdown rather than added on, so they must not be counted twice here.
    const clockMs = sprint ? s.totalMs : s.clockMs;
    const booked = finishSession(nextTotals, sprint ? null : clockMs);
    setTotals(booked);
    saveTotals(mode.id, booked);

    const at = Date.now();
    // Into the records straight away: the round trip can take its time.
    setRuns(rs => [...rs, { mode: mode.id, at, clockMs, found: s.correct }]);

    const payload = {
      playerToken: getPlayerToken(),
      mode: mode.id,
      at,
      clockMs,
      totalMs: s.totalMs,
      penaltyMs: s.penaltyMs,
      found: s.correct,
      missed: s.wrong,
      skipped: s.skipped,
      // The log is newest-first; a run reads in the order it was played.
      boards: [...finalLog].reverse().map(toBoardRecord),
    };
    api.postDrillSession(payload).catch(() => queuePending(payload));

    setDeal(null);
    setEndsAt(null);
  }

  /** The countdown ran out with a board still on screen: that board was never answered. */
  function handleTimeUp() {
    if (!deal) return;
    setDeal(null);
    setEndsAt(null);
    endRun(log, totals);
  }

  useEffect(() => {
    if (!deal || endsAt === null) return;
    const left = endsAt - Date.now();
    if (left <= 0) { handleTimeUp(); return; }
    const id = setTimeout(handleTimeUp, left);
    return () => clearTimeout(id);
  });

  /** End the current board — answered, missed or skipped — and move the session on. */
  function finishBoard(current: Deal, found: Cell[], miss: Cell | null, skipped = false) {
    const attempt: Attempt = {
      code: current.code,
      regions: current.regions,
      solution: current.solution,
      mode: mode.id,
      placed: found,
      miss,
      skipped,
      correct: miss === null && !skipped,
      ms: Date.now() - shownAt,
    };
    const nextLog = [attempt, ...log];
    const streak = attempt.correct ? stats.streak + 1 : 0;
    const nextTotals = addAttempt(totals, attempt, streak);

    setLog(nextLog);
    // Three outcomes, three rings: a skip costs no penalty, so it must not wear the miss's.
    setFlash(attempt.correct ? 'right' : attempt.skipped ? 'skip' : 'wrong');

    // A sprint runs until its clock does. A miss pulls that clock closer rather than adding to
    // one, and a miss with less than the penalty left ends the run there and then.
    if (sprint) {
      const deadline = (endsAt ?? Date.now()) - (attempt.correct || attempt.skipped ? 0 : WRONG_PENALTY_MS);
      setEndsAt(deadline);
      setTotals(nextTotals);
      saveTotals(mode.id, nextTotals);
      if (deadline <= Date.now()) endRun(nextLog, nextTotals); else nextBoard();
      return;
    }

    // Only finding a board shortens a session: a miss or a skip just deals another one. Only a
    // run played to the end is booked — quitting halfway isn't a run, and isn't recorded.
    if (attempt.correct && stats.correct + 1 >= SESSION_LENGTH) {
      endRun(nextLog, nextTotals);
    } else {
      setTotals(nextTotals);
      saveTotals(mode.id, nextTotals);
      nextBoard();
    }
  }

  function handlePick(row: number, col: number) {
    if (!deal) return;
    const cell = { row, col };
    const now = Date.now();
    // The tail of a double-tap on the square just answered, now sitting over a fresh board.
    // Refused rather than scored — and deliberately not re-armed, so the window runs from the
    // first tap and a flurry can't lock the square.
    if (isEchoTap(lastPick.current, cell, now)) {
      setIgnored(prev => ({ row, col, n: (prev?.n ?? 0) + 1 }));
      return;
    }
    lastPick.current = { cell, at: now };
    if (isWanted(deal.solution, cell)) finishBoard(deal, [cell], null);
    else finishBoard(deal, [], cell);
  }

  /**
   * Pass on this board.
   *
   * Not a wrong answer — accuracy is over boards actually answered — but it still costs one of the
   * session's five, so skipping everything scores 0/5 rather than a clean sheet.
   */
  function handleSkip() {
    if (!deal) return;
    lastPick.current = null; // nothing was clicked, so nothing is guarded on the next board
    finishBoard(deal, [], null, true);
  }

  // The listener is registered once; a ref keeps it looking at the current board rather than the
  // one that was on screen when it was attached.
  const handlerRef = useRef<(e: KeyboardEvent) => void>(() => {});
  handlerRef.current = (e: KeyboardEvent) => {
    if (practiceAt !== null) return; // the practice board owns the keyboard while it is up
    if (review) {
      if (e.key === 'Escape') { e.preventDefault(); setReview(null); }
      return;
    }
    if (deal) {
      if (e.key === 's' || e.key === 'S') { e.preventDefault(); handleSkip(); }
      return;
    }
    // Space/Enter starts a session and begins the next one. Answering is the board's job.
    if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); startSession(); }
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => handlerRef.current(e);
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Anything an earlier visit couldn't send — including history left behind by the version that
  // kept it in the browser — goes up when the drill is opened, and the records come back down.
  useEffect(() => {
    let live = true;
    migrateLocalHistory(getPlayerToken());
    flushPending(api.postDrillSession)
      .catch(() => {})
      .then(() => api.getDrillSessions(getPlayerToken()))
      // Runs finished while this was in flight are already local; they keep their place.
      .then(r => { if (live) setRuns(rs => [...r.sessions, ...rs]); })
      .catch(() => {}); // no records to show beats no drill to play
    return () => { live = false; };
  }, []);

  /** Every version's records come off the one list, so switching version costs no round trip. */
  const records = useMemo(() => bestRuns(runs, mode), [runs, mode]);

  // The server holds the sessions, so its records win; the local copy is what shows when the
  // fetch didn't land. Without this the summary and the cards could disagree about the same run —
  // a browser with no local totals would claim a new record the server knew wasn't one.
  // A sprint's record is a board count, which only the server-side history keeps; a session's has
  // the local totals as a fallback for when the fetch didn't land.
  const allTimeBest = records.allTime ?? (sprint ? null : totals.bestTimeMs);
  const formatScore = (v: number) => (sprint ? String(v) : formatMs(v));
  /** What is worth sitting down with afterwards: the boards that beat you. */
  const unfinished = useMemo(() => log.filter(a => !a.correct).reverse(), [log]);

  // Rendered in place of the drill, not beside it: the drill's own state stays mounted, so
  // coming back lands on the same summary with the same log.
  if (practiceAt !== null && unfinished[practiceAt]) {
    const board = unfinished[practiceAt];
    const left = unfinished.length - practiceAt - 1;
    return (
      <PracticeView
        key={`${practiceAt}-${board.code}`}
        regions={board.regions}
        backLabel="Tek Mode"
        onNext={left > 0 ? () => setPracticeAt(practiceAt + 1) : undefined}
        nextLabel={left > 1 ? `Next of ${left} left` : 'Last one'}
        onExit={() => {
          setPracticeAt(null);
          // A board left running behind the practice view would come back with all that time on
          // it, and its time is the measurement this whole thing records.
          if (deal) setShownAt(Date.now());
        }}
      />
    );
  }

  return (
    <div id="view-drill">
      <header className="game-header">
        <div className="game-header-inner">
          <button className="btn-back" onClick={onBack}>←<span className="hide-mobile"> Puzzles</span></button>
          <h2>
            <span className="game-title-name is-worded">Tek Mode</span>
          </h2>
          <div className="game-nav" />
        </div>
      </header>

      <main className="drill-main">
        {!deal && <ModePicker mode={mode} onChange={onChangeMode} />}

        {!deal && !finished && (
          <div className="drill-start">
            <h3>No Xs allowed. Just place a queen where it goes.</h3>
            <div className="completion-row">
              <button className="btn-primary" onClick={startSession} disabled={!ready}>
                {ready ? 'Start session' : 'Loading boards…'}
              </button>
              <button className="btn-secondary" onClick={onOpenHistory}>Session history</button>
            </div>
            {deckError && (
              <p className="error">
                Couldn't load this version's boards. <button className="link-btn" onClick={() => setDeckTry(n => n + 1)}>Try again</button>
              </p>
            )}
          </div>
        )}

        {deal && (
          <>
            {!sprint && <Pips found={stats.correct} />}
            <div
              className="drill-board"
              style={{ '--drill-n': String(mode.size) } as React.CSSProperties}
            >
              <StaticBoard regions={deal.regions} />
              {/* Keyed on the attempt count so every answer restarts the fade from full color.
                  Transitioning one element's color instead spends the first frames of a miss
                  looking like the hit before it — at drill speed that is most of the flash. */}
              {flash && (
                <span key={log.length} className={`drill-flash is-${flash}`} aria-hidden="true">
                  {flash === 'wrong' && (
                    <span className="drill-penalty">{sprint ? '−' : '+'}{WRONG_PENALTY_MS / 1000}s</span>
                  )}
                </span>
              )}
              {/* Marks the square, not the board: it is that one click that went nowhere, and the
                  board itself is untouched. Keyed so a second refused tap flashes again. */}
              {ignored && (
                <span
                  key={ignored.n}
                  className="drill-ignored"
                  aria-hidden="true"
                  style={{
                    '--drill-r': String(ignored.row),
                    '--drill-c': String(ignored.col),
                  } as React.CSSProperties}
                />
              )}
              {/* Answering is a transparent grid over a read-only board: a click here is an answer,
                  not a mark, and there is nothing to undo. */}
              <div className="drill-hits">
                {Array.from({ length: mode.size * mode.size }, (_, i) => {
                  const row = (i / mode.size) | 0, col = i % mode.size;
                  return (
                    <button
                      key={i}
                      className="drill-hit"
                      onClick={() => handlePick(row, col)}
                      aria-label={`Place a queen on row ${row + 1}, column ${col + 1}`}
                    />
                  );
                })}
              </div>
            </div>

            <div className="drill-tools">
              <button className="btn-icon" onClick={handleSkip}>Skip board <kbd>S</kbd></button>
            </div>

            <div className="drill-live">
              {sprint && endsAt !== null ? <Countdown endsAt={endsAt} /> : <LiveTimer startedAt={shownAt} running />}
              <span className="drill-progress">
                {sprint
                  ? `${stats.correct} found`
                  : `${stats.correct} of ${SESSION_LENGTH} found`}
              </span>
              <span className="drill-session-clock">
                {!sprint && <>session <LiveTimer startedAt={shownAt} running banked={stats.clockMs} className="" /></>}
                {stats.wrong > 0 && `${sprint ? '' : ' · '}${stats.wrong} missed (${sprint ? '−' : '+'}${stats.penaltyMs / 1000}s)`}
                {stats.skipped > 0 && ` · ${stats.skipped} skipped`}
              </span>
              {stats.streak > 1 && <span className="drill-streak">🔥 {stats.streak} in a row</span>}
            </div>
            <p className="drill-hint">Click any queen</p>
          </>
        )}

        {finished && (
          <div className="drill-summary">
            {!sprint && <Pips found={stats.correct} />}
            <p className="drill-score">
              {sprint ? `${stats.correct} board${stats.correct === 1 ? '' : 's'}` : formatMs(stats.clockMs)}
            </p>
            {/* The time on the boards is already the score, minus the penalties — so the only thing
                left worth saying under it is what the mistakes cost. Nothing to say, no line. */}
            {sprint ? (
              <p className="drill-score-sub">
                in {mode.limitMs! / 1000}s
                {stats.wrong > 0 && <> · <strong>{stats.wrong}</strong> missed <strong>−{stats.penaltyMs / 1000}s</strong></>}
                {stats.skipped > 0 && <> · <strong>{stats.skipped}</strong> skipped</>}
              </p>
            ) : stats.wrong > 0 && (
              <p className="drill-score-sub"><strong>+{stats.penaltyMs / 1000}s</strong> from mistakes</p>
            )}
            <p className="drill-summary-note">
              {sprint
                ? (records.allTime !== null && stats.correct >= records.allTime
                    ? <>Your best run yet.</>
                    : <>Best so far: <strong>{records.allTime}</strong> boards.</>)
                : (allTimeBest !== null && stats.clockMs <= allTimeBest
                    ? <>Your fastest five yet.</>
                    : <>Fastest five so far: <strong>{formatMs(allTimeBest!)}</strong>.</>)}
            </p>
            <div className="completion-row">
              <button className="btn-primary" onClick={startSession}>{sprint ? 'Go again' : 'Play another five'}</button>
              <button className="btn-secondary" onClick={onOpenHistory}>Session history</button>
            </div>
            {unfinished.length > 0 && (
              <div className="drill-retry">
                <p className="drill-retry-title">
                  Try playing the one{unfinished.length === 1 ? '' : 's'} you missed as a normal board:
                </p>
                <div className="drill-retry-list">
                  {unfinished.map((a, i) => (
                    <button key={i} className="drill-retry-item" onClick={() => setPracticeAt(i)}>
                      <span className="drill-thumb"><StaticBoard regions={a.regions} /></span>
                      <span className="drill-retry-label">{a.skipped ? 'skipped' : 'missed'}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Accuracy for the run in progress, then the two clocks worth chasing. Both records are
            per board size and count a full session only, penalties and all. */}
        <div className="drill-stats">
          <div className="prof-stat" title={`${stats.correct} right of the ${stats.answered} answered this session`}>
            <div className="prof-stat-value">{stats.answered ? `${Math.round(stats.accuracy * 100)}%` : '—'}</div>
            <div className="prof-stat-label">Right</div>
          </div>
          <div className="prof-stat" title={`Best ${mode.label} run finished today`}>
            <div className="prof-stat-value">{records.today !== null ? formatScore(records.today) : '—'}</div>
            <div className="prof-stat-label">Today's best</div>
            {sprint && <div className="prof-stat-sub">boards</div>}
          </div>
          <div className="prof-stat" title={`Best ${mode.label} run ever`}>
            <div className="prof-stat-value">{allTimeBest !== null ? formatScore(allTimeBest) : '—'}</div>
            <div className="prof-stat-label">All time best</div>
            {sprint && <div className="prof-stat-sub">boards</div>}
          </div>
        </div>

        <LogStrip log={log} onReview={setReview} />
      </main>

      {review && (
        <div className="help-modal-overlay" onClick={() => setReview(null)}>
          <div className="help-modal drill-review" onClick={e => e.stopPropagation()}>
            <h3>{review.correct ? 'You had it' : review.skipped ? 'Skipped' : 'Missed it'}</h3>
            <div
              className="drill-review-board"
              style={{ '--drill-n': String(review.regions.length) } as React.CSSProperties}
            >
              <StaticBoard
                regions={review.regions}
                queens={solutionCells(review.solution)}
                mark={review.miss}
                ringed={new Set(review.placed.map(cellKey))}
              />
            </div>
            <p className="settings-note">
              {review.correct
                ? `Found in ${formatMs(review.ms)}.`
                : review.skipped
                  ? `Passed on after ${formatMs(review.ms)}.`
                  : `The ✕ is where you clicked — no queen goes there. ${formatMs(review.ms)}.`}
            </p>
            <div className="completion-row">
              {!review.correct && (
                <button
                  className="btn-primary"
                  onClick={() => { setPracticeAt(unfinished.indexOf(review)); setReview(null); }}
                >
                  Play this board
                </button>
              )}
              <button className="btn-secondary" onClick={() => setReview(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

